import axios from "axios";
import { getUsertoken } from "@/utils/auth";

function getAuthHeaders() {
  const token = getUsertoken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const BASE = process.env.VUE_APP_BASE_URL + "comisiones/";

export default {
  namespaced: true,
  state: () => ({
    preventistas: [],
    preventista: null,
    config: null,
    rangos: {
      margen: [],
      volumen: [],
      categoria: [],
    },
    stats: null,
    historial: [],
    loading: false,
    loadingStats: false,
    error: null,
  }),
  getters: {
    allPreventistas: (state) => state.preventistas,
    currentPreventista: (state) => state.preventista,
    currentConfig: (state) => state.config,
    rangosMargen: (state) => state.rangos.margen,
    rangosVolumen: (state) => state.rangos.volumen,
    rangosCategoria: (state) => state.rangos.categoria,
    currentStats: (state) => state.stats,
    historial: (state) => state.historial,
    isLoading: (state) => state.loading,
    isLoadingStats: (state) => state.loadingStats,
    hasError: (state) => state.error,
  },
  mutations: {
    SET_PREVENTISTAS(state, list) {
      state.preventistas = list;
    },
    SET_PREVENTISTA(state, item) {
      state.preventista = item;
    },
    SET_CONFIG(state, config) {
      state.config = config;
    },
    SET_RANGOS(state, { tipo, rangos }) {
      state.rangos[tipo] = rangos;
    },
    SET_STATS(state, stats) {
      state.stats = stats;
    },
    SET_HISTORIAL(state, historial) {
      state.historial = historial;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_LOADING_STATS(state, value) {
      state.loadingStats = value;
    },
    SET_ERROR(state, value) {
      state.error = value;
    },
  },
  actions: {
    async fetchPreventistas({ commit }) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.get(BASE + "preventistas", {
          headers: getAuthHeaders(),
        });
        commit("SET_PREVENTISTAS", data.data || []);
        commit("SET_ERROR", null);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || "Error al cargar preventistas");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchPreventista({ commit }, id) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.get(BASE + `preventistas/${id}`, {
          headers: getAuthHeaders(),
        });
        commit("SET_PREVENTISTA", data.data || null);
        commit("SET_ERROR", null);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || "Error al cargar preventista");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updatePreventistaConfig({ commit }, { id, ...payload }) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.put(
          BASE + `preventistas/${id}/config`,
          payload,
          { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
        );
        commit("SET_PREVENTISTA", data.data || null);
        commit("SET_ERROR", null);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || "Error al actualizar config");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchConfig({ commit }) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.get(BASE + "config", {
          headers: getAuthHeaders(),
        });
        commit("SET_CONFIG", data.data || null);
        commit("SET_ERROR", null);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || "Error al cargar configuración");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateConfig({ commit }, payload) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.put(BASE + "config", payload, {
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        });
        commit("SET_CONFIG", data.data || null);
        commit("SET_ERROR", null);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || "Error al actualizar configuración");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchRangos({ commit }, tipo) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.get(BASE + `rangos/${tipo}`, {
          headers: getAuthHeaders(),
        });
        commit("SET_RANGOS", { tipo, rangos: data.data || [] });
        commit("SET_ERROR", null);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || `Error al cargar rangos de ${tipo}`);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async createRango({ dispatch, commit }, { tipo, ...payload }) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.post(BASE + `rangos/${tipo}`, payload, {
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        });
        await dispatch("fetchRangos", tipo);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || `Error al crear rango de ${tipo}`);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateRango({ dispatch, commit }, { tipo, id, ...payload }) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.put(BASE + `rangos/${tipo}/${id}`, payload, {
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        });
        await dispatch("fetchRangos", tipo);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || `Error al actualizar rango de ${tipo}`);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async deleteRango({ dispatch, commit }, { tipo, id }) {
      try {
        commit("SET_LOADING", true);
        const { data } = await axios.delete(BASE + `rangos/${tipo}/${id}`, {
          headers: getAuthHeaders(),
        });
        await dispatch("fetchRangos", tipo);
        return data;
      } catch (error) {
        commit("SET_ERROR", error.message || `Error al eliminar rango de ${tipo}`);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchStats({ commit }, { id, periodo_inicio, periodo_fin } = {}) {
      try {
        commit("SET_LOADING_STATS", true);
        const params = {};
        if (periodo_inicio) params.periodo_inicio = periodo_inicio;
        if (periodo_fin) params.periodo_fin = periodo_fin;
        const { data } = await axios.get(BASE + `preventistas/${id}/stats`, {
          headers: getAuthHeaders(),
          params,
        });
        commit("SET_STATS", data.data || null);
        return data;
      } catch (error) {
        throw error;
      } finally {
        commit("SET_LOADING_STATS", false);
      }
    },

    async fetchHistorial({ commit }, id) {
      try {
        commit("SET_LOADING_STATS", true);
        const { data } = await axios.get(BASE + `preventistas/${id}/stats/historial`, {
          headers: getAuthHeaders(),
        });
        commit("SET_HISTORIAL", data.data || []);
        return data;
      } catch (error) {
        throw error;
      } finally {
        commit("SET_LOADING_STATS", false);
      }
    },
  },
};
